import React, { useState } from 'react';
import Icon from '../AppIcon';
import Button from './Button';

const RegistrationProgress = ({
  currentStep = 1,
  completedSteps = [],
  onStepClick,
  onSave,
  onNext,
  onPrevious,
  isLoading = false,
  hasUnsavedChanges = false
}) => {
  const [showStepDetails, setShowStepDetails] = useState(false);

  const steps = [
    {
      id: 1,
      title: 'Basic Information',
      description: 'Business name, contact details, and location',
      icon: 'User',
      sections: ['Business Details', 'Contact Information', 'Location']
    },
    {
      id: 2,
      title: 'Services & Specialization',
      description: 'What you offer and your cultural expertise',
      icon: 'Briefcase',
      sections: ['Service Categories', 'Cultural Specializations', 'Pricing']
    },
    {
      id: 3,
      title: 'Portfolio & Gallery',
      description: 'Showcase your work and previous events',
      icon: 'Camera',
      sections: ['Photo Gallery', 'Video Portfolio', 'Client Testimonials']
    },
    {
      id: 4,
      title: 'Availability & Booking',
      description: 'Calendar setup and booking preferences',
      icon: 'Calendar',
      sections: ['Calendar Integration', 'Booking Settings', 'Availability Rules']
    },
    {
      id: 5,
      title: 'Verification & Launch',
      description: 'Document verification and profile review',
      icon: 'Shield',
      sections: ['Document Upload', 'Profile Review', 'Go Live']
    }
  ];

  const getStepStatus = (stepId) => {
    if (completedSteps.includes(stepId)) return 'completed';
    if (stepId === currentStep) return 'current';
    if (stepId < currentStep) return 'accessible';
    return 'locked';
  };

  const getStepIcon = (step) => {
    const status = getStepStatus(step.id);
    if (status === 'completed') return 'CheckCircle';
    if (status === 'current') return step.icon;
    return step.icon;
  };

  const getStepColor = (stepId) => {
    const status = getStepStatus(stepId);
    switch (status) {
      case 'completed': return '#228B22'; // success
      case 'current': return '#8B4513'; // primary
      case 'accessible': return '#6B5B4F'; // text-secondary
      default: return '#8B7B6F'; // text-muted
    }
  };

  const handleStepClick = (stepId) => {
    const status = getStepStatus(stepId);
    if (status === 'accessible' || status === 'completed' || status === 'current') {
      onStepClick?.(stepId);
    }
  };

  const handleSave = () => {
    onSave?.(currentStep);
  };

  const handleNext = () => {
    onNext?.(currentStep);
  };

  const handlePrevious = () => {
    onPrevious?.(currentStep);
  };

  const currentStepData = steps.find(step => step.id === currentStep);
  const progressPercentage = (completedSteps.length / steps.length) * 100;

  return (
    <div className="bg-white border-b border-border shadow-soft">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Desktop Progress */}
        <div className="hidden lg:block py-6">
          {/* Progress Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="font-heading font-bold text-2xl text-text-primary">
                Vendor Registration
              </h2>
              <p className="text-text-secondary font-caption mt-1">
                Complete your profile to start receiving bookings
              </p>
            </div>
            
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <div className="text-sm font-medium text-text-primary">
                  {completedSteps.length} of {steps.length} completed
                </div>
                <div className="text-xs text-text-secondary font-caption">
                  {Math.round(progressPercentage)}% complete
                </div>
              </div>
              <div className="w-32 h-2 bg-surface-200 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-primary to-secondary transition-all duration-500"
                  style={{ width: `${progressPercentage}%` }}
                />
              </div>
            </div>
          </div>

          {/* Step Navigation */}
          <div className="flex items-center justify-between">
            {steps.map((step, index) => {
              const status = getStepStatus(step.id);
              const isClickable = status === 'accessible' || status === 'completed' || status === 'current';
              
              return (
                <div key={step.id} className="flex items-center flex-1">
                  {/* Step Circle */}
                  <button
                    onClick={() => handleStepClick(step.id)}
                    disabled={!isClickable}
                    className={`relative flex items-center justify-center w-12 h-12 rounded-full border-2 transition-smooth ${
                      status === 'completed'
                        ? 'bg-success border-success text-success-foreground'
                        : status === 'current' ?'bg-primary border-primary text-primary-foreground'
                        : status === 'accessible' ?'bg-white border-text-secondary text-text-secondary hover:border-primary hover:text-primary' :'bg-surface-100 border-border text-text-muted cursor-not-allowed'
                    } ${isClickable ? 'hover:scale-105' : ''}`}
                  >
                    <Icon 
                      name={getStepIcon(step)} 
                      size={20} 
                      color="currentColor" 
                    />
                    
                    {hasUnsavedChanges && status === 'current' && (
                      <div className="absolute -top-1 -right-1 w-3 h-3 bg-warning rounded-full border-2 border-white" />
                    )}
                  </button>

                  {/* Step Info */}
                  <div className="ml-4 flex-1">
                    <div className={`font-medium text-sm ${
                      status === 'current' ? 'text-primary' : 
                      status === 'completed'? 'text-success' : 'text-text-secondary'
                    }`}>
                      {step.title}
                    </div>
                    <div className="text-xs text-text-muted font-caption">
                      {step.description}
                    </div>
                  </div>

                  {/* Connector Line */}
                  {index < steps.length - 1 && (
                    <div className={`flex-1 h-0.5 mx-4 ${
                      completedSteps.includes(step.id) ? 'bg-success' : 'bg-border'
                    }`} />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Mobile Progress */}
        <div className="lg:hidden py-4">
          {/* Mobile Header */}
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={() => setShowStepDetails(!showStepDetails)}
              className="flex items-center space-x-2 text-text-primary"
            >
              <span className="font-heading font-semibold">
                Step {currentStep} of {steps.length}
              </span>
              <Icon 
                name={showStepDetails ? "ChevronUp" : "ChevronDown"} 
                size={16} 
                color="currentColor" 
              />
            </button>
            
            <div className="text-sm text-text-secondary font-data">
              {Math.round(progressPercentage)}%
            </div>
          </div>

          {/* Mobile Progress Bar */}
          <div className="w-full h-2 bg-surface-200 rounded-full overflow-hidden mb-4">
            <div 
              className="h-full bg-gradient-to-r from-primary to-secondary transition-all duration-500"
              style={{ width: `${progressPercentage}%` }}
            />
          </div>

          {/* Current Step Info */}
          <div className="mb-4">
            <h3 className="font-heading font-semibold text-lg text-primary">
              {currentStepData?.title}
            </h3>
            <p className="text-sm text-text-secondary font-caption">
              {currentStepData?.description}
            </p>
          </div>

          {/* Mobile Step Details */}
          {showStepDetails && (
            <div className="space-y-2 mb-4 animate-slide-down">
              {steps.map((step) => {
                const status = getStepStatus(step.id);
                const isClickable = status === 'accessible' || status === 'completed' || status === 'current';
                
                return (
                  <button
                    key={step.id}
                    onClick={() => handleStepClick(step.id)}
                    disabled={!isClickable}
                    className={`w-full flex items-center space-x-3 p-3 rounded-lg text-left transition-smooth ${
                      status === 'current' ?'bg-primary-50 border border-primary-200'
                        : status === 'completed' ?'bg-success-50 border border-success-200'
                        : isClickable
                        ? 'bg-surface-50 hover:bg-surface-100' :'bg-surface-50 opacity-50 cursor-not-allowed'
                    }`}
                  >
                    <Icon 
                      name={getStepIcon(step)} 
                      size={18} 
                      color={getStepColor(step.id)} 
                    />
                    <div className="flex-1">
                      <div className={`font-medium text-sm ${
                        status === 'current' ? 'text-primary' : 
                        status === 'completed'? 'text-success' : 'text-text-secondary'
                      }`}>
                        {step.title}
                      </div>
                      <div className="text-xs text-text-muted font-caption">
                        {step.sections.join(' • ')}
                      </div>
                    </div>
                    {status === 'completed' && (
                      <Icon name="CheckCircle" size={16} color="#228B22" />
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Action Bar */}
        <div className="flex items-center justify-between py-4 border-t border-border-light">
          <div className="flex items-center space-x-4">
            {hasUnsavedChanges && (
              <div className="flex items-center space-x-2 text-warning">
                <Icon name="AlertCircle" size={16} color="currentColor" />
                <span className="text-sm font-caption">Unsaved changes</span>
              </div>
            )}
          </div>

          <div className="flex items-center space-x-3">
            <Button
              variant="ghost"
              size="md"
              onClick={handleSave}
              disabled={isLoading || !hasUnsavedChanges}
              iconName="Save"
              iconSize={16}
            >
              Save Progress
            </Button>
            
            {currentStep > 1 && (
              <Button
                variant="outline"
                size="md"
                onClick={handlePrevious}
                disabled={isLoading}
                iconName="ArrowLeft"
                iconSize={16}
              >
                Previous
              </Button>
            )}
            
            <Button
              variant="primary"
              size="md"
              onClick={handleNext}
              disabled={isLoading}
              loading={isLoading}
              iconName="ArrowRight"
              iconPosition="right"
              iconSize={16}
            >
              {currentStep === steps.length ? 'Complete Registration' : 'Next Step'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegistrationProgress;